// src/pages/Goals.tsx
import React, { useEffect, useState, useCallback } from 'react';
import { useLookups } from '../context/LookupContext';
import { useAppContext } from '../context/AppContext';
import { deleteGoalRecord, fetchGoalData, saveOrUpdateGoalRecord } from '../services/goalService';
import { fetchDependents } from '../services/assetService';
import { LookupValue } from '../services/lookupService';
import { RefreshCw } from 'lucide-react';

// Import your new components
import Notification from '../components/shared/Notification';
import GoalHeader from '../components/goals/GoalHeader';
import GoalForm from '../components/goals/GoalForm';
import ActionBar from '../components/goals/ActionBar';
import GoalsTable from '../components/goals/GoalsTable';
import GoalsMobileView from '../components/goals/GoalsMobileView';
import Pagination from '../components/shared/Pagination';
import EmptyState from '../components/goals/EmptyState';

export interface GoalData {
  id: string;
  goalOwnerType: string;
  goalType: string;
  goalSubType: string;
  goalName: string;
  goalTarget: string;
  goalTargetTenure: string;
  goalCompletionTentativeAmount: string;
  status: string;
  familyMemberId?: string;
}

export type ColumnKey = keyof GoalData | 'ownerTypeLabel' | 'typeLabel' | 'subTypeLabel' | 'targetLabel';

const visibleColumns: ColumnKey[] = [
  'goalOwnerType', 'goalName', 'goalType', 'goalSubType',
  'goalTarget', 'goalTargetTenure', 'goalCompletionTentativeAmount'
];

const getColumnDisplayName = (col: ColumnKey): string => {
  const displayNames: Record<ColumnKey, string> = {
    id: 'ID', goalOwnerType: 'Owner Type', goalType: 'Goal Type', goalSubType: 'Sub Type',
    goalName: 'Goal Name', goalTarget: 'Target Amount', goalTargetTenure: 'Tenure (Years)',
    goalCompletionTentativeAmount: 'Tentative Amount', status: 'Status', familyMemberId: '',
    ownerTypeLabel: 'Owner Type', typeLabel: 'Goal Type', subTypeLabel: 'Sub Type', targetLabel: 'Target Amount'
  };
  return displayNames[col];
};

const Goals: React.FC = () => {
    const { state } = useAppContext();
    const { getOptions, getLabel, isLoading: isLookupsLoading } = useLookups();

    const [transformedData, setTransformedData] = useState<GoalData[]>([]);
    const [dependents, setDependents] = useState<{ id: string; name: string }[]>([]);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [isFormSubmitted, setIsFormSubmitted] = useState(false);
    const [apiLoading, setApiLoading] = useState(true);
    const [operationStatus, setOperationStatus] = useState({ show: false, message: '', type: 'success' as 'success' | 'error' });
    const [formData, setFormData] = useState<GoalData>({
        id: '', goalOwnerType: '', goalType: '', goalSubType: '', goalName: '', goalTarget: '',
        goalTargetTenure: '', goalCompletionTentativeAmount: '', status: '', familyMemberId: ''
    });
    const [dropdownOptions, setDropdownOptions] = useState<{ ownerType: LookupValue[]; goalType: LookupValue[]; goalSubType: LookupValue[]; }>({
        ownerType: [], goalType: [], goalSubType: []
    });
    const [loadingSubTypes, setLoadingSubTypes] = useState(false);
    const [sortConfig, setSortConfig] = useState<{ key: ColumnKey; direction: 'asc' | 'desc' | null; }>({ key: 'goalName', direction: null });
    const [searchTerm, setSearchTerm] = useState('');
    const [columnFilters, setColumnFilters] = useState<Partial<Record<ColumnKey, string>>>({});
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [selectedRows, setSelectedRows] = useState<string[]>([]);
    
    const lookupGroupMap: Record<string, string> = {
        goalOwnerType: 'ownerType', goalType: 'goalType', goalSubType: 'goalSubType',
    };

    const formatCurrency = useCallback((value: string | number) => {
        const num = typeof value === 'string' ? parseFloat(String(value).replace(/,/g, '')) : value;
        if (isNaN(num)) return 'N/A';
        const symbol = state.currencySymbol || '₹';
        return `${symbol}${new Intl.NumberFormat('en-IN').format(num)}`;
    }, [state.currencySymbol]);

    const resetForm = () => {
        setFormData({
            id: '', goalOwnerType: '', goalType: '', goalSubType: '', goalName: '', goalTarget: '',
            goalTargetTenure: '', goalCompletionTentativeAmount: '', status: '', familyMemberId: ''
        });
        setEditingId(null);
    };

    const fetchGoalFromAPI = useCallback(async () => {
        setApiLoading(true);
        try {
            const apiData = await fetchGoalData();
            setTransformedData(apiData.data as GoalData[]);
        } catch (error) {
            console.error('Failed to fetch goal data:', error);
            setOperationStatus({ show: true, message: 'Failed to fetch goals.', type: 'error' });
        } finally {
            setApiLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchGoalFromAPI();
    }, [fetchGoalFromAPI]);

    useEffect(() => {
        if (!isLookupsLoading) {
            setDropdownOptions({
                ownerType: getOptions('ownerType'),
                goalType: getOptions('goalType'),
                goalSubType: [],
            });
        }
    }, [isLookupsLoading, getOptions]);

    useEffect(() => {
        const typeVal = formData.goalType;
        if (!typeVal) {
            setDropdownOptions(prev => ({ ...prev, goalSubType: [] }));
            return;
        }
        setLoadingSubTypes(true);
        const allSubTypes = getOptions('goalSubType');
        const filteredSubs = allSubTypes.filter(sub => sub.parentValue === typeVal);
        setDropdownOptions(prev => ({ ...prev, goalSubType: filteredSubs }));
        setLoadingSubTypes(false);
    }, [formData.goalType, getOptions]);

    useEffect(() => {
        fetchDependents().then(setDependents).catch(err => console.error(err));
    }, []);

    useEffect(() => {
        if (operationStatus.show) {
            const timer = setTimeout(() => setOperationStatus({ show: false, message: '', type: 'success' }), 5000);
            return () => clearTimeout(timer);
        }
    }, [operationStatus]);

    // --- ALL HANDLERS ARE NOW FULLY IMPLEMENTED ---

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value, ...(name === 'goalOwnerType' && { familyMemberId: '' }) }));
    };
    
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData.goalOwnerType || !formData.goalName || !formData.goalType || !formData.goalTarget) {
            return alert('Please fill in all required fields marked with *');
        }
        if (formData.goalOwnerType === '2' && !formData.familyMemberId) {
            return alert('Please select a family member for a "Family" goal.');
        }
        setIsFormSubmitted(true);
        try {
            await saveOrUpdateGoalRecord(formData);
            setShowForm(false);
            resetForm();
            await fetchGoalFromAPI();
            setOperationStatus({ show: true, message: `Goal ${editingId ? 'updated' : 'created'} successfully!`, type: 'success' });
        } catch (error: any) {
            setOperationStatus({ show: true, message: error.message || 'Failed to save goal.', type: 'error' });
        } finally {
            setIsFormSubmitted(false);
        }
    };
    
    const handleSort = (key: ColumnKey) => {
        let direction: 'asc' | 'desc' = sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
        setSortConfig({ key, direction });
        const sortedData = [...transformedData].sort((a, b) => {
            const group = lookupGroupMap[key];
            let aValue: string | number = group ? getLabel(group, a[key as keyof GoalData] as string) : a[key as keyof GoalData] ?? '';
            let bValue: string | number = group ? getLabel(group, b[key as keyof GoalData] as string) : b[key as keyof GoalData] ?? '';

            if (['goalTarget', 'goalTargetTenure', 'goalCompletionTentativeAmount'].includes(key)) {
                aValue = parseFloat(aValue as string || '0');
                bValue = parseFloat(bValue as string || '0');
            }

            if (aValue < bValue) return direction === 'asc' ? -1 : 1;
            if (aValue > bValue) return direction === 'asc' ? 1 : -1;
            return 0;
        });
        setTransformedData(sortedData);
    };
    
    const handleEdit = (goal: GoalData) => {
        setFormData({ ...goal });
        setEditingId(goal.id);
        setShowForm(true);
    };

    const handleDelete = async (id: string) => {
        if (window.confirm('Are you sure you want to delete this goal?')) {
            try {
                await deleteGoalRecord(id);
                setOperationStatus({ show: true, message: 'Goal deleted successfully!', type: 'success' });
                fetchGoalFromAPI(); // Refresh data
            } catch (error: any) {
                setOperationStatus({ show: true, message: error.message || 'Failed to delete goal.', type: 'error' });
            }
        }
    };
    
    const handleRowSelect = (id: string) => {
        setSelectedRows(prev => prev.includes(id) ? prev.filter(rowId => rowId !== id) : [...prev, id]);
    };

    const handleSelectAll = () => {
        if (selectedRows.length === paginatedData.length && paginatedData.length > 0) {
            setSelectedRows([]);
        } else {
            setSelectedRows(paginatedData.map(g => g.id));
        }
    };
    
    const handleBulkDelete = async () => {
        if (window.confirm(`Are you sure you want to delete ${selectedRows.length} selected goals?`)) {
            try {
                await Promise.all(selectedRows.map(id => deleteGoalRecord(id)));
                setOperationStatus({ show: true, message: 'Selected goals deleted successfully!', type: 'success' });
                setSelectedRows([]);
                fetchGoalFromAPI();
            } catch (error: any) {
                setOperationStatus({ show: true, message: error.message || 'Failed to delete goals.', type: 'error' });
            }
        }
    };
    
    

    const filteredData = transformedData.filter(goal => {
        const getDisplayValue = (colKey: keyof GoalData) => {
            const group = lookupGroupMap[colKey];
            return group ? getLabel(group, goal[colKey] as string) : goal[colKey] || '';
        };
        const searchMatch = searchTerm ? Object.keys(goal).some(key =>
            String(getDisplayValue(key as keyof GoalData)).toLowerCase().includes(searchTerm.toLowerCase())
        ) : true;
        const filterMatch = Object.entries(columnFilters).every(([key, value]) =>
            !value || String(getDisplayValue(key as keyof GoalData)).toLowerCase().includes(String(value).toLowerCase())
        );
        return searchMatch && filterMatch;
    });

    const totalItems = filteredData.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedData = filteredData.slice(startIndex, endIndex);
    const totalGoals = transformedData.reduce((sum, goal) => sum + parseFloat(goal.goalTarget || '0'), 0);
    
    const paginationProps = {
        currentPage, totalPages, totalItems, itemsPerPage, startIndex, endIndex,
        handlePageChange: setCurrentPage,
        handleItemsPerPageChange: (e: React.ChangeEvent<HTMLSelectElement>) => {
            setItemsPerPage(parseInt(e.target.value));
            setCurrentPage(1);
        },
    };
    
    const getSortIndicator = (key: ColumnKey) => (sortConfig.key === key ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '');
    const handleAddClick = () => { resetForm(); setShowForm(true); };

    return (
        <div className="space-y-8 p-4 sm:p-6">
            <Notification status={operationStatus} onClose={() => setOperationStatus({ ...operationStatus, show: false })} />

            <GoalHeader
                totalGoals={totalGoals}
                recordCount={filteredData.length}
                apiLoading={apiLoading}
                onAdd={handleAddClick}
                formatCurrency={formatCurrency}
            />

            {showForm && (
                <GoalForm
                    formData={formData}
                    editingId={editingId}
                    isFormSubmitted={isFormSubmitted}
                    dropdownOptions={dropdownOptions}
                    dependents={dependents}
                    loadingSubTypes={loadingSubTypes}
                    handleSubmit={handleSubmit}
                    handleInputChange={handleInputChange}
                    handleSelectChange={handleSelectChange}
                    onCancel={() => { setShowForm(false); resetForm(); }}
                />
            )}

            <GoalsMobileView
                paginatedData={paginatedData}
                apiLoading={apiLoading}
                searchTerm={searchTerm}
                onSearchChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                handleEdit={handleEdit}
                handleDelete={handleDelete}
                getLabel={getLabel}
                formatCurrency={formatCurrency}
                paginationProps={paginationProps}
            />
              {filteredData.length >0 && (
            <div className="hidden md:block bg-white rounded-xl shadow-lg overflow-x-auto">
                <ActionBar
                    searchTerm={searchTerm}
                    onSearchChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                    onBulkDelete={handleBulkDelete}
                    selectedRowCount={selectedRows.length}
                />
              {apiLoading ? (
                    <div  className="text-center py-12"><RefreshCw className="w-6 h-6 animate-spin text-green-600" /></div>
                    ) : (
                    <GoalsTable
                        paginatedData={paginatedData}
                        visibleColumns={visibleColumns}
                        getColumnDisplayName={getColumnDisplayName}
                        getSortIndicator={getSortIndicator}
                        handleSort={handleSort}
                        columnFilters={columnFilters}
                        setColumnFilters={setColumnFilters}
                        selectedRows={selectedRows}
                        handleSelectAll={handleSelectAll}
                        handleRowSelect={handleRowSelect}
                        handleEdit={handleEdit}
                        handleDelete={handleDelete}
                        formatCurrency={formatCurrency}
                        getLabel={getLabel}
                        lookupGroupMap={lookupGroupMap}
                    />
                )}
                <Pagination {...paginationProps} />
            </div>
            )}

            {filteredData.length === 0 && !apiLoading && (
                <EmptyState onAdd={handleAddClick} />
            )}
        </div>
    );
};

export default Goals;